import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric, Select } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import useImageUploader from "../../../../components/ui/ImageUploader";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { SubstructureFamily } from "../../../../http/deckDesigner/types/substructureFamilies";
import { SubstructureMaterial } from "../../../../http/deckDesigner/types/substructureMaterials";
import {
  InsertSubstructureBody,
  Substructure,
  SUBSTRUCTURE_COUNTRIES,
  SubstructureCountry,
  UpdateSubstructureBody,
} from "../../../../http/deckDesigner/types/substructures";
import useDeckSubstructureFamiliesApi from "../../../../http/deckDesigner/useSubstructureFamilies";
import useDeckSubstructureMaterialsApi from "../../../../http/deckDesigner/useSubstructureMaterials";
import useDeckSubstructuresApi from "../../../../http/deckDesigner/useSubstructures";
import useItemOptions, { getItemOptionLabel, ItemOption, toItemOption } from "../../../../hooks/useItemOptions";

interface SubstructureFormBody {
  item: ItemOption | null;
  substructureFamily: SubstructureFamily | null;
  substructureMaterials: SubstructureMaterial[];
  base: number | null;
  height: number | null;
  secondMomentOfArea: number | null;
  countries: SubstructureCountry[];
}

interface SubstructureFormProps {
  substructure?: Substructure;
  onClose: () => void;
}

const SubstructureForm: FC<SubstructureFormProps> = ({ substructure, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const substructuresApi = useDeckSubstructuresApi();
  const substructureFamiliesApi = useDeckSubstructureFamiliesApi();
  const substructureMaterialsApi = useDeckSubstructureMaterialsApi();
  const itemOptions = useItemOptions();
  const substructureFamilyOptions = useAsyncOptions(substructureFamiliesApi.getDeckSubstructureFamilies);
  const substructureMaterialOptions = useAsyncOptions(substructureMaterialsApi.getDeckSubstructureMaterials);

  const defaultValues: SubstructureFormBody = {
    item: substructure?.item ? toItemOption(substructure.item) : null,
    substructureFamily: substructure?.substructureFamily ?? null,
    substructureMaterials: substructure?.substructureMaterials ?? [],
    base: substructure?.base ?? null,
    height: substructure?.height ?? null,
    secondMomentOfArea: substructure?.secondMomentOfArea ?? null,
    countries: substructure?.countries ?? ["all"],
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: substructure?.image ?? undefined,
    isLoading: false,
  });

  const onSubmit = () => async (formData: SubstructureFormBody) => {
    if (!formData.substructureMaterials.length || formData.height == null) return;

    const body: InsertSubstructureBody | UpdateSubstructureBody = {
      itemId: formData.item?.id ?? null,
      substructureFamilyCode: formData.substructureFamily?.code ?? null,
      substructureMaterialCodes: formData.substructureMaterials.map((m) => m.code),
      base: formData.base,
      height: formData.height,
      secondMomentOfArea: formData.secondMomentOfArea,
      countries: formData.countries,
    };

    try {
      if (substructure) {
        const image = await substructuresApi.uploadImage(substructure.id, newImage, removeImage);
        await substructuresApi.updateDeckSubstructure(substructure.id, { ...body, ...image });
      } else {
        const createdSubstructure = await substructuresApi.insertDeckSubstructure(body);

        // The image is stored under the substructure id, so it can only be uploaded once created.
        if (newImage) {
          const image = await substructuresApi.uploadImage(createdSubstructure.id, newImage, false);
          await substructuresApi.updateDeckSubstructure(createdSubstructure.id, { ...body, ...image });
        }
      }

      onSave();
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        substructure
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.substructures.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.substructures.singular}`
      }
    >
      <Form<SubstructureFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <Controller
              name="item"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      {...itemOptions}
                      inputId={labelId}
                      isMulti={false}
                      isClearable
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={getItemOptionLabel}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="substructureFamily"
              control={control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureFamilies.singular}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId }) => (
                    <AsyncSelect<SubstructureFamily, false>
                      {...field}
                      {...substructureFamilyOptions}
                      inputId={labelId}
                      isMulti={false}
                      isClearable
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="substructureMaterials"
              control={control}
              rules={{ validate: (value) => !!value.length }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureMaterials.plural}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<SubstructureMaterial, true>
                      {...substructureMaterialOptions}
                      inputId={labelId}
                      value={field.value}
                      isMulti={true}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="countries"
              control={control}
              rules={{ validate: (value) => !!value.length }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.deckDesignerModule.substructures.countries} error={fieldState.error}>
                  {({ labelId }) => (
                    <Select<SubstructureCountry, true>
                      inputId={labelId}
                      value={field.value}
                      options={SUBSTRUCTURE_COUNTRIES}
                      isMulti={true}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) =>
                        i18n.translation.deckDesignerModule.substructures.countryOptions[option]
                      }
                      getOptionValue={(option) => option}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="base"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructures.base}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="height"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.height} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="secondMomentOfArea"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructures.secondMomentOfArea}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SubstructureForm;
